import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import RoleGuard from "@/components/auth/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  ArrowRight,
  Filter,
  Ship,
} from "lucide-react";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { Toaster } from "@/components/ui/toaster";
import { PageErrorBoundary } from "@/components/fallbacks";
import { PageLoader } from "@/components/loading/PageLoader";
import type { PurchaseOrder } from "@/features/purchase-orders";
import { useOrderQueries } from "@/features/purchase-orders/hooks/useOrderQueries";
import { useOrderMutations } from "@/features/purchase-orders/hooks/useOrderMutations";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { BackToTopPill } from "@/components/BackToTopPill";
import { PageActionBar, PageActionBarDivider } from "@/components/layout/PageActionBar";
import { STATUS_SELECT_CLASS } from "@/features/purchase-orders/utils/status-style";
import { formatDay, useDateFnsLocale } from "@/lib/format-dates";
import {
  formatStatusLabel,
  getOrderReceiptTotals,
  getShipmentStatusClassName,
  getValidStatusesForOrder,
  validateGuardedStatusChange,
} from "@/features/purchase-orders/utils/order-status";

type SortField = "po_number" | "status" | "submitted_date" | "created_at";
type SortOrder = "asc" | "desc";
type StatusFilter = PurchaseOrder["status"] | "all";

const PAGE_SIZE = 50;

// BLU-574 parity: "Active" = all in-flight transfers (everything except
// received/cancelled). Drafts included so a just-created transfer stays visible
// after the create → detail → back flow.
const ACTIVE_STATUS_FILTERS: PurchaseOrder["status"][] = [
  "draft",
  "submitted",
  "in_transit",
  "partially_received",
];

// The transfer chain: draft → submitted → in_transit → partially_received → received.
const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "in_transit", label: "In Transit" },
  { value: "partially_received", label: "Partial" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

const TRANSFER_STATUS_UPDATE_OPTIONS: PurchaseOrder["status"][] = [
  "submitted",
  "in_transit",
  "partially_received",
  "received",
  "cancelled",
];

const TransfersList = () => {
  const navigate = useNavigate();
  const dateLocale = useDateFnsLocale();
  const { updateOrderStatus } = useOrderMutations();
  // `allLocations`, not `locations`: this map RESOLVES A NAME on stored
  // transfers, whose source or destination may since have been delegated.
  const { allLocations, isLoading: locationsLoading } = useLocations();

  usePageTitle("Transfers", "Move stock between your locations");

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState(1);
  const [actioningOrderId, setActioningOrderId] = useState<string | null>(null);

  const queryStatus = statusFilter === "all" ? undefined : statusFilter;
  const queryStatuses =
    activeOnly && statusFilter === "all" ? ACTIVE_STATUS_FILTERS : undefined;
  const {
    orders,
    pagination,
    loading: ordersLoading,
    isFetching: ordersFetching,
    error: ordersError,
  } = useOrderQueries({
    page,
    pageSize: PAGE_SIZE,
    // Hard-locked to transfers — this page never shows purchase orders.
    orderType: "transfer",
    status: queryStatus,
    statuses: queryStatuses,
    searchTerm: search,
    sortBy,
    sortOrder,
  });

  const locationNameById = useMemo(() => {
    return new Map(allLocations.map((location) => [location.id, location.name]));
  }, [allLocations]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortOrder("asc");
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    return sortOrder === "asc"
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const totalPages = Math.max(1, pagination.totalPages);
  const safePage = Math.min(page, totalPages);
  const startItem = pagination.total === 0 ? 0 : pagination.offset + 1;
  const endItem = pagination.total === 0 ? 0 : pagination.offset + orders.length;

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const runOrderAction = async (orderId: string, fn: () => Promise<void>) => {
    setActioningOrderId(orderId);
    try {
      await fn();
    } finally {
      setActioningOrderId(null);
    }
  };

  const handleGuardedStatusChange = async (
    order: PurchaseOrder,
    newStatus: PurchaseOrder["status"]
  ) => {
    const guard = validateGuardedStatusChange(order, newStatus);
    if (!guard.valid) {
      toast({ title: guard.title, description: guard.description });
      navigate(`/transfers/${order.id}`);
      return;
    }

    await runOrderAction(order.id, async () => {
      await updateOrderStatus(order.id, newStatus, order.location_id);
    });
  };

  if (ordersLoading || locationsLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="transfers" />
        </div>
      </RoleGuard>
    );
  }

  if (ordersError) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md">
          <h2 className="text-xl font-semibold text-red-700 mb-2">
            Error Loading Data
          </h2>
          <p className="text-red-600">
            {ordersError?.message || "Failed to load data"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          <PageActionBar
            ariaLabel="Transfers actions"
            left={
              <>
                <div className="relative w-56">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search transfer, status..."
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    className="h-8 pl-9 text-xs"
                  />
                </div>
                <Filter className="h-4 w-4 text-muted-foreground self-center" aria-hidden="true" />
                <Select
                  value={statusFilter}
                  onValueChange={(value) => {
                    setStatusFilter(value as StatusFilter);
                    if (value !== "all") {
                      setActiveOnly(false);
                    }
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[160px] text-xs">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <PageActionBarDivider />
                <Button
                  variant={activeOnly ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "h-8 px-3 text-xs gap-1.5 shrink-0",
                    activeOnly && "border-green-600 bg-green-600 text-white hover:border-green-700 hover:bg-green-700"
                  )}
                  onClick={() => {
                    setActiveOnly((prev) => !prev);
                    setStatusFilter("all");
                    setPage(1);
                  }}
                >
                  Active
                </Button>
              </>
            }
            right={
              <Button
                size="sm"
                onClick={() => navigate("/transfers/new")}
                className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              >
                <ArrowLeftRight className="h-3.5 w-3.5" />
                New transfer
              </Button>
            }
          />

          <div className="bg-card rounded-lg shadow border mt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button onClick={() => handleSort("po_number")} className="flex items-center hover:text-foreground">
                      Transfer # <SortIcon field="po_number" />
                    </button>
                  </TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead>
                    <button onClick={() => handleSort("status")} className="flex items-center hover:text-foreground">
                      Status <SortIcon field="status" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button onClick={() => handleSort("created_at")} className="flex items-center hover:text-foreground">
                      Created <SortIcon field="created_at" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button onClick={() => handleSort("submitted_date")} className="flex items-center hover:text-foreground">
                      Submitted <SortIcon field="submitted_date" />
                    </button>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Arrival ETA</TableHead>
                  <TableHead>
                    <div className="flex items-center">
                      <Ship className="h-4 w-4 mr-1" />
                      Shipment
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      {activeOnly ? (
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-muted-foreground">No active transfers</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              setActiveOnly(false);
                              setPage(1);
                            }}
                          >
                            Show all transfers
                          </Button>
                        </div>
                      ) : search.trim() || statusFilter !== "all" ? (
                        "No matching transfers found"
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-muted-foreground">No transfers yet</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => navigate("/transfers/new")}
                          >
                            Create your first transfer
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => {
                    // Match OrdersList: status is directly editable except for the
                    // draft / cancelled / in_transit terminal-ish states.
                    const showStatusSelect =
                      order.status !== "draft" &&
                      order.status !== "cancelled" &&
                      order.status !== "in_transit";
                    const isMutating = actioningOrderId === order.id;
                    const validStatuses = getValidStatusesForOrder(order, TRANSFER_STATUS_UPDATE_OPTIONS);
                    const { totalOrdered } = getOrderReceiptTotals(order);
                    const sourceName = locationNameById.get(order.source_location_id ?? "") || "-";
                    const destName = locationNameById.get(order.location_id ?? "") || "-";

                    return (
                      <TableRow
                        key={order.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/transfers/${order.id}`)}
                      >
                        <TableCell>
                          <Link
                            to={`/transfers/${order.id}`}
                            className="group block w-fit max-w-full"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <span className="font-medium text-primary group-hover:underline">
                              {order.po_number}
                            </span>
                            {order.title ? (
                              <div className="max-w-[220px] truncate text-xs text-muted-foreground">
                                {order.title}
                              </div>
                            ) : null}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="truncate max-w-[140px]">{sourceName}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                            <span className="truncate max-w-[140px] font-medium">{destName}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {totalOrdered.toLocaleString()}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          {showStatusSelect ? (
                            <Select
                              value={order.status}
                              onValueChange={(value) =>
                                void handleGuardedStatusChange(order, value as PurchaseOrder["status"])
                              }
                              disabled={isMutating}
                            >
                              <SelectTrigger
                                className={cn(
                                  "w-[145px] h-8 border-0 text-[11px] font-medium",
                                  STATUS_SELECT_CLASS[order.status]
                                )}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {validStatuses.map((status) => (
                                  <SelectItem key={status} value={status} className="text-[11px]">
                                    {formatStatusLabel(status)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div
                              className={cn(
                                "w-[145px] h-8 rounded-md px-3 text-[11px] font-medium inline-flex items-center",
                                STATUS_SELECT_CLASS[order.status]
                              )}
                            >
                              {formatStatusLabel(order.status)}
                            </div>
                          )}
                        </TableCell>
                        {/* All three dates share formatDay so a row reads one format (as OrdersList). arrival_eta_date
                            is a calendar DATE; formatDay reads it as local midnight so it shows on its own day. */}
                        <TableCell>{order.created_at ? formatDay(order.created_at, dateLocale) : "-"}</TableCell>
                        <TableCell>{order.submitted_date ? formatDay(order.submitted_date, dateLocale) : "-"}</TableCell>
                        <TableCell>
                          {order.arrival_eta_date ? formatDay(order.arrival_eta_date, dateLocale) : "-"}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          {(order.shipments ?? []).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {(order.shipments ?? []).map((shipment) => (
                                <Badge
                                  key={shipment.id}
                                  className={cn(
                                    "cursor-pointer text-xs",
                                    getShipmentStatusClassName(shipment.status)
                                  )}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/shipments/${shipment.id}`);
                                  }}
                                >
                                  {shipment.shipment_number ?? "Shipment"}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {pagination.total > PAGE_SIZE ? (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {startItem} to {endItem} of {pagination.total} transfers
                </p>
                <div className="flex space-x-2">
                  <span className="inline-flex items-center px-2 text-sm text-muted-foreground">
                    Page {safePage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1 || ordersFetching}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages || ordersFetching}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </main>

        <Toaster />

        <BackToTopPill />
      </div>
    </RoleGuard>
  );
};

export default function TransfersListWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <TransfersList />
    </PageErrorBoundary>
  );
}
